import React, { useEffect, useState } from 'react'

import { BaseView, BaseText } from '@/components/base'
import { WalletCurrency } from '@/domain'
import { useUserStore } from '@/store'
import { currencyUtils } from '@/utils'
import { useTheme } from '@/hooks/useTheme'

const { formatFiatCurrency } = currencyUtils

interface AnimatedDonutChartProps {
  values: WalletCurrency[]
}

export const AnimatedDonutChart: React.FC<AnimatedDonutChartProps> = ({
  values,
}) => {
  const theme = useTheme()
  const wallet = useUserStore((state) => state.wallet)
  const displayCurrencyCode = wallet?.displayCurrency || 'USD'

  const [animationProgress, setAnimationProgress] = useState(0)

  // Calculate total value
  const totalValue = values.reduce(
    (sum, currency) => sum + currency.fiatAmount,
    0,
  )
  const formattedTotalValue = formatFiatCurrency({
    value: totalValue,
    fiatCode: displayCurrencyCode,
    showDecimals: true,
  })

  // Animate on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationProgress(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [values])

  // SVG properties - increased size for better visibility
  const size = 320 // Increased from 284 to make it more prominent
  const center = size / 2
  const radius = 150 // Increased proportionally from 132
  const strokeWidth = 12 // Slightly increased stroke width
  const circumference = 2 * Math.PI * radius

  // Calculate segments for multi-colored donut
  let cumulativePercentage = 0
  const segments = values.map((currency) => {
    const percentage =
      totalValue > 0 ? (currency.fiatAmount / totalValue) * 100 : 0
    const startAngle = (cumulativePercentage / 100) * 360
    const endAngle = ((cumulativePercentage + percentage) / 100) * 360

    cumulativePercentage += percentage

    return {
      ...currency,
      percentage,
      startAngle,
      endAngle,
      strokeDasharray: `${
        (percentage / 100) * circumference * animationProgress
      } ${circumference}`,
      strokeDashoffset:
        -((cumulativePercentage - percentage) / 100) * circumference,
      color: Array.isArray(currency.avatarBGRefColor)
        ? currency.avatarBGRefColor[0]
        : currency.avatarBGRefColor || theme.colors.background.secondaryGray,
    }
  })

  return (
    <BaseView
      center
      style={{
        width: `${size}px`,
        height: `${size}px`,
        position: 'relative',
      }}
    >
      {/* SVG Donut Chart */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          transform: 'rotate(-90deg)', // Start from top
        }}
      >
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={`${theme.colors.background.secondaryGray}30`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        {/* Currency segments - stacked approach */}
        {segments.map((segment, index) => (
          <circle
            key={segment.id || index}
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={segment.color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={segment.strokeDasharray}
            strokeDashoffset={segment.strokeDashoffset}
            style={{
              transition:
                'stroke-dasharray 1s ease-in-out, stroke-dashoffset 1s ease-in-out',
            }}
          />
        ))}
      </svg>

      {/* Center text */}
      <BaseView
        center
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
        }}
      >
        <BaseText
          fontSize={44}
          fontFamily="bold"
          color="label.default"
          style={{
            textAlign: 'center',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {formattedTotalValue}
        </BaseText>
      </BaseView>
    </BaseView>
  )
}
